import { type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Ornament } from "./Ornament";

interface AuthLayoutProps {
  kicker: string;
  title: ReactNode;
  tagline: string;
  children: ReactNode;
}

export function AuthLayout({ kicker, title, tagline, children }: AuthLayoutProps) {
  return (
    <div className="relative min-h-screen">
      <div className="mx-auto grid min-h-screen max-w-[1480px] grid-cols-1 md:grid-cols-[0.85fr_1.15fr]">
        <aside className="relative flex flex-col justify-between border-b border-ink/20 bg-parchment-deep p-10 md:border-b-0 md:border-r md:p-16">
          <div className="animate-fade-in">
            <Link to="/login" className="block">
              <div className="label mb-3 text-ink-walnut">Volume I</div>
              <div className="font-display text-6xl font-light leading-none text-ink md:text-7xl">
                <span className="display-italic">Maison</span>
              </div>
              <div className="label mt-3 text-ink-walnut">a kitchen journal</div>
            </Link>
          </div>

          <figure
            className="my-12 hidden max-w-md animate-fade-in opacity-0 md:block"
            style={{ animationDelay: "500ms" }}
          >
            <div className="label mb-6 text-sienna">From the colophon</div>
            <blockquote className="font-display text-3xl font-light italic leading-tight text-ink/85">
              “Tell me what you eat, and I shall tell you who you are.”
            </blockquote>
            <figcaption className="label mt-6 text-ink-walnut">
              Brillat-Savarin, 1826
            </figcaption>
          </figure>

          <div className="flex items-end justify-between">
            <Ornament className="h-4 w-20 text-sienna" />
            <span className="label text-ink-muted">Est. MMXXVI</span>
          </div>
        </aside>

        <section className="relative flex flex-col justify-center px-8 py-16 md:px-20 md:py-24">
          <div className="w-full max-w-lg">
            <div className="label mb-8 text-ink-walnut animate-fade-up">
              {kicker}
            </div>
            <h1
              className="display-1 animate-fade-up"
              style={{ animationDelay: "80ms" }}
            >
              {title}
            </h1>
            <p
              className="mt-8 max-w-md font-body text-xl italic leading-snug text-ink-walnut animate-fade-up"
              style={{ animationDelay: "160ms" }}
            >
              {tagline}
            </p>
            <span
              className="hairline-ink my-10 origin-left animate-rule-sweep"
              style={{ animationDelay: "240ms" }}
            />
            {children}
          </div>
        </section>
      </div>
    </div>
  );
}
